// FILE: src/pages/ClientPortal/ClientDashboard.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { clientPortalAPI } from '../../api/services';
import './ClientPortal.css';

const fmtTime = (d) => d ? new Date(d).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : '—';
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : '—';
function statusBadge(s) {
  const map = { Scheduled:'scheduled', Completed:'completed', 'In Progress':'inprogress', Missed:'missed' };
  return <span className={`cp-badge cp-badge-${map[s]||'default'}`}>{s || 'Unknown'}</span>;
}
function elapsed(from) {
  if (!from) return '';
  const diff = Math.floor((new Date() - new Date(from)) / 1000);
  const h = Math.floor(diff/3600), m = Math.floor((diff%3600)/60), s = diff%60;
  return `${h}h ${m}m ${s}s`;
}
const avatarColors = ['#1a4d8c','#059669','#d97706','#7c3aed','#db2777'];
const initials = (name='') => name.split(' ').map(w=>w[0]).join('').toUpperCase().slice(0,2);

export default function ClientDashboard() {
  const { user } = useAuth();
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [clock,   setClock]   = useState(new Date());

  useEffect(() => { const t = setInterval(() => setClock(new Date()), 1000); return () => clearInterval(t); }, []);

  const load = useCallback(async () => {
    try {
      const res = await clientPortalAPI.getDashboard();
      setData(res.data.data);
    } catch { toast.error('Failed to load dashboard.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="cp-page"><div style={{color:'#6b7280'}}>Loading dashboard…</div></div>;

  const { todayVisits=[], nextVisit, liveCaregiver, stats={} } = data || {};

  return (
    <div className="cp-page">
      <div className="cp-header">
        <h1 className="cp-title">Good {greeting()}, {user?.firstName || 'there'} 👋</h1>
        <p className="cp-subtitle">{clock.toLocaleDateString('en-US', { weekday:'long', year:'numeric', month:'long', day:'numeric' })}</p>
      </div>

      {/* Live Caregiver Banner */}
      {liveCaregiver && (
        <div className="cp-live-card">
          <div className="cp-live-pulse" />
          <div>
            <div className="cp-live-name">🧑‍⚕️ {liveCaregiver.CaregiverName} is currently with you</div>
            <div className="cp-live-since">Clocked in at {fmtTime(liveCaregiver.ClockInTime)}</div>
          </div>
          <div className="cp-live-elapsed">{elapsed(liveCaregiver.ClockInTime)}</div>
        </div>
      )}

      {/* Next Visit */}
      {nextVisit && (
        <div className="cp-next-card">
          <div className="cp-next-label">⏰ Next Scheduled Visit</div>
          <div className="cp-next-name">{nextVisit.CaregiverName}</div>
          <div className="cp-next-time">{fmtDate(nextVisit.StartTime)} at {fmtTime(nextVisit.StartTime)}</div>
          <div className="cp-next-service">{nextVisit.ServiceName}</div>
        </div>
      )}

      {/* Stats */}
      <div className="cp-stats-grid">
        <div className="cp-stat-card" style={{'--accent':'#1a4d8c'}}>
          <span className="cp-stat-icon">📅</span>
          <span className="cp-stat-value">{stats.totalThisMonth || 0}</span>
          <span className="cp-stat-label">Visits This Month</span>
        </div>
        <div className="cp-stat-card" style={{'--accent':'#059669'}}>
          <span className="cp-stat-icon">✅</span>
          <span className="cp-stat-value">{stats.completedThisMonth || 0}</span>
          <span className="cp-stat-label">Completed</span>
        </div>
        <div className="cp-stat-card" style={{'--accent':'#f59e0b'}}>
          <span className="cp-stat-icon">📋</span>
          <span className="cp-stat-value">{todayVisits.length}</span>
          <span className="cp-stat-label">Today's Visits</span>
        </div>
        <div className="cp-stat-card" style={{'--accent':'#dc2626'}}>
          <span className="cp-stat-icon">❌</span>
          <span className="cp-stat-value">{stats.missedThisMonth || 0}</span>
          <span className="cp-stat-label">Missed</span>
        </div>
      </div>

      {/* Today's Visits */}
      {todayVisits.length > 0 && (
        <div className="cp-section">
          <div className="cp-section-title">📅 Today's Visits</div>
          {todayVisits.map((v, i) => (
            <div key={v.ID || i} className="cp-card">
              <div className="cp-card-left">
                <div className="cp-card-name">{v.CaregiverName}</div>
                <div className="cp-card-meta">🕐 {fmtTime(v.StartTime)} – {fmtTime(v.EndTime)}</div>
                {v.ServiceName && <div className="cp-card-meta">🏥 {v.ServiceName}</div>}
                {v.ClockInTime && <div className="cp-card-meta" style={{color:'#059669'}}>✅ Clocked in at {fmtTime(v.ClockInTime)}</div>}
                {v.ClockOutTime && <div className="cp-card-meta" style={{color:'#dc2626'}}>🔴 Clocked out at {fmtTime(v.ClockOutTime)}</div>}
              </div>
              <div className="cp-card-right">{statusBadge(v.VisitStatus)}</div>
            </div>
          ))}
        </div>
      )}

      {!liveCaregiver && !nextVisit && todayVisits.length === 0 && (
        <div className="cp-empty">
          <div className="cp-empty-icon">📭</div>
          <div className="cp-empty-text">No visits today</div>
          <div className="cp-empty-sub">You have no scheduled visits for today.</div>
        </div>
      )}
    </div>
  );
}

function greeting() {
  const h = new Date().getHours();
  return h < 12 ? 'Morning' : h < 17 ? 'Afternoon' : 'Evening';
}
